import { useState, useEffect, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const LANG_OPTIONS = [
  { code: "zh-Hant", label: "繁" },
  { code: "zh-Hans", label: "简" },
  { code: "en", label: "EN" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const { t, i18n } = useTranslation("common");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const homeNavLinks = [
    { label: t("nav.brandVision"), href: "#identity" },
    { label: t("nav.strategicPositioning"), href: "#strategy" },
    { label: t("nav.coreBusiness"), href: "#business" },
    { label: t("nav.competitiveAdvantage"), href: "#advantage" },
    { label: t("nav.developmentRoadmap"), href: "#roadmap" },
    { label: t("nav.careers"), href: "#careers" },
  ];

  const storyNavLinks = [
    { label: t("nav.backToHome"), href: "/" },
    { label: t("nav.brandStory"), href: "/story" },
  ];

  const navLinks = isHome ? homeNavLinks : storyNavLinks;

  const currentLang = LANG_OPTIONS.find((l) => l.code === i18n.language) ?? LANG_OPTIONS[0];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md border-b border-ax-silver-light"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-6 md:px-12 flex items-center justify-between h-16 md:h-20">
        <Link to="/" className="flex items-center gap-2 md:gap-3">
          <img
            src="https://static.readdy.ai/image/1c927f723c274043d230f8a1015ea0aa/a8cf771ba2b07c3ccb8e4bb89c36eb5d.png"
            alt="AX Axis Logo"
            className="h-8 md:h-10 w-auto object-contain"
          />
          <span
            className={`font-semibold text-sm md:text-base tracking-wide transition-colors ${
              scrolled ? "text-ax-dark" : "text-white"
            }`}
          >
            AX AXIS INTERNATIONAL HOLDING LIMITED
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => {
            const isHash = link.href.startsWith("#");
            const isStoryActive = location.pathname === link.href;
            const baseClasses = `text-sm font-medium tracking-wide transition-colors hover:text-ax-red ${
              scrolled ? "text-ax-dark/80" : "text-white/80"
            }`;
            return isHash ? (
              <a key={link.href} href={link.href} className={baseClasses}>
                {link.label}
              </a>
            ) : (
              <Link
                key={link.href}
                to={link.href}
                className={`${baseClasses} ${isStoryActive ? "text-ax-red" : ""}`}
              >
                {link.label}
              </Link>
            );
          })}

          {/* Language Switcher */}
          <div className="relative" ref={langRef}>
            <button
              onClick={() => setLangOpen(!langOpen)}
              className={`flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-full border transition-colors cursor-pointer whitespace-nowrap ${
                scrolled
                  ? "border-ax-silver-light text-ax-dark/70 hover:border-ax-red/40"
                  : "border-white/30 text-white/70 hover:border-white/60"
              }`}
            >
              <i className="ri-global-line text-sm" />
              <span>{currentLang.label}</span>
              <i className={`ri-arrow-down-s-line text-xs transition-transform ${langOpen ? "rotate-180" : ""}`} />
            </button>
            {langOpen && (
              <div className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-lg border border-ax-silver-light py-2 min-w-[100px] z-50">
                {LANG_OPTIONS.map((opt) => (
                  <button
                    key={opt.code}
                    onClick={() => {
                      i18n.changeLanguage(opt.code);
                      setLangOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-ax-light transition-colors cursor-pointer ${
                      opt.code === i18n.language ? "text-ax-red font-medium" : "text-ax-dark/70"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <button
          className="md:hidden w-8 h-8 flex items-center justify-center cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <i
            className={`ri-menu-line text-xl ${
              scrolled ? "text-ax-dark" : "text-white"
            }`}
          />
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-white border-b border-ax-silver-light px-6 py-4">
          {navLinks.map((link) => {
            const isHash = link.href.startsWith("#");
            return isHash ? (
              <a
                key={link.href}
                href={link.href}
                className="block py-3 text-sm font-medium text-ax-dark/80 border-b border-ax-light last:border-0"
                onClick={() => setMenuOpen(false)}
              >
                {link.label}
              </a>
            ) : (
              <Link
                key={link.href}
                to={link.href}
                className="block py-3 text-sm font-medium text-ax-dark/80 border-b border-ax-light last:border-0"
                onClick={() => setMenuOpen(false)}
              >
                {link.label}
              </Link>
            );
          })}
          {/* Mobile Language Switcher */}
          <div className="flex items-center gap-2 pt-3">
            <span className="text-xs text-ax-dark/40">语言</span>
            {LANG_OPTIONS.map((opt) => (
              <button
                key={opt.code}
                onClick={() => {
                  i18n.changeLanguage(opt.code);
                  setMenuOpen(false);
                }}
                className={`px-3 py-1 text-xs rounded-full border transition-colors cursor-pointer whitespace-nowrap ${
                  opt.code === i18n.language
                    ? "bg-ax-red text-white border-ax-red"
                    : "text-ax-dark/60 border-ax-silver-light hover:border-ax-dark/30"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}